import { get, isString } from 'lodash-es';

import { captureException } from 'config/sentryConfig';
import { redactSensitiveUrl } from 'config/sentryEventContext';

type LogError = (data: unknown, place?: string, apiCallEnabled?: boolean) => void;

const toError = (data: unknown): Error => {
  if (data instanceof Error) {
    return data;
  }

  if (typeof data === 'object' && data && 'error' in data) {
    const nested = (data as { error: unknown }).error;
    if (nested instanceof Error) {
      return nested;
    }
  }

  const message = get(data, 'message') || get(data, 'customMessage') || 'Unknown error';
  return new Error(String(message));
};

const isCapturableError = (data: unknown): boolean => {
  if (data instanceof Error) {
    return true;
  }
  return typeof data === 'object' && !!data && 'error' in data && (data as { error: unknown }).error instanceof Error;
};

const logError: LogError = (data, place = 'unknown place', apiCallEnabled = false) => {
  const isProduction = import.meta.env.VITE_ENVIRONMENT === 'production';

  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.error(`[logError:${place}]`, data);
    return;
  }

  try {
    if (isCapturableError(data)) {
      captureException(toError(data), {
        tags: { source: 'logError', place },
        extra: {
          place,
          href: redactSensitiveUrl(window.location.href),
          customMessage: get(data, 'customMessage'),
        },
        level: 'error',
      });
    }
  } catch {
    // Reporting must never break the app.
  }

  if (apiCallEnabled) {
    fetch('https://app.mojamatura.edu.pl/err/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: `The error occurs in ${place}`,
        date: new Date(),
        message: get(data, 'message', 'empty message'),
        customMessage: get(data, 'customMessage'),
        url: redactSensitiveUrl(window.location.href),
        errorData: isString(data) ? data : undefined,
      }),
    });
  }
};

export default logError;
